// ユーザー管理ページ（React）
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
} from '@mui/material';
import { contentLabel } from '../constants/contents';

// const API_URL = 'http://localhost:8000/api/users'; // FastAPI側のURLに合わせて
// const Select_API_URL = 'http://127.0.0.1:8000/api/select_user'; // FastAPI側のURLに合わせて
const Select_API_URL = '/api/select_user'; // FastAPI側のURLに合わせて
// const Regist_API_URL = 'http://127.0.0.1:8000/api/add_user'; // FastAPI側のURLに合わせて
const Regist_API_URL = '/api/add_user'; // FastAPI側のURLに合わせて
// const Delete_API_URL = 'http://127.0.0.1:8000/api/delete_user'; // FastAPI側のURLに合わせて
const Delete_API_URL = '/api/delete_user'; // FastAPI側のURLに合わせて
// メール送信基盤を持たないため、パスワードリセットは管理者による仮パスワード発行で代替する
const Reset_Password_API_URL = (userId) => `/api/admin/users/${userId}/reset_password`;
// 利用状況（ログイン数・有料会員数）。ログインはログイン成功時に KeibaAuditLog へ書かれる
const Usage_Stats_API_URL = '/api/admin/usage_stats';
const LOGIN_STATS_DAYS = 14;

// 管理APIは管理者トークンを要求するため、必ず Authorization を付ける
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
});

// 401/403/409 などをそのまま握り潰すと、操作が無反応に見えるため文言を出す
const notifyError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  alert(detail || fallback);
};

// 一覧は全件返さずページングする（本番303件）。値は backend の
// modules/register.py: DEFAULT_PAGE_SIZE / MAX_PAGE_SIZE と揃えること
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];

// 課金状況（Issue #240）。値はサーバの KeibaSubscription.status。契約が無ければ null で空欄にする
const BILLING_STATUS_LABELS = {
  active: '契約中',
  past_due: '支払い遅延',
  canceled: '解約済み',
};
const billingStatusLabel = (status) => (status ? BILLING_STATUS_LABELS[status] || status : '');
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('ja-JP') : '');
const formatDateTime = (value) => (value ? new Date(value).toLocaleString('ja-JP') : '');

// 利用状況の数字1つぶん
const StatCard = ({ label, value, note }) => (
  <Paper sx={{ p: 2, minWidth: 160 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
    {note && (
      <Typography variant="caption" color="text.secondary">
        {note}
      </Typography>
    )}
  </Paper>
);

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [form, setForm] = useState({ email: '', username: '', password: '' });
  // 発行した仮パスワードはここにしか現れない。ダイアログを閉じたら二度と取得できない
  const [issued, setIssued] = useState(null);

  // ページ送り・表示件数の変更で取り直す。useCallback にしないと毎レンダーで
  // 関数が作り直され、useEffect の依存が毎回変わって取得が無限に走る
  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(Select_API_URL, {
        ...authConfig(),
        params: { limit: rowsPerPage, offset: page * rowsPerPage },
      });
      setUsers(res.data.items);
      setTotal(res.data.total ?? res.data.items.length);
    } catch (error) {
      notifyError(error, 'ユーザー一覧の取得に失敗しました');
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ユーザー一覧のページ送りとは独立。開いたときに1回だけ取る
  const [usageStats, setUsageStats] = useState(null);
  const [usageStatsError, setUsageStatsError] = useState(false);
  useEffect(() => {
    axios
      .get(Usage_Stats_API_URL, { ...authConfig(), params: { days: LOGIN_STATS_DAYS } })
      .then((res) => setUsageStats(res.data))
      .catch(() => setUsageStatsError(true));
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRegister = async () => {
    // user_id は空欄なら送らない。DB の KeibaAIUser_id_seq に採番させる（#31 残タスク6）。
    // 以前は無条件に Number('') = NaN を送っており、空欄だと JSON が壊れていた。
    const payload = { ...form };
    if (String(form.user_id ?? '').trim() === '') {
      delete payload.user_id;
    } else {
      payload.user_id = Number(form.user_id);
    }
    try {
      await axios.post(Regist_API_URL, payload, authConfig());
    } catch (error) {
      notifyError(error, 'ユーザーの登録に失敗しました');
      return;
    }
    // setForm({ email: '', username: '', password: '' });
    fetchUsers();
  };

  const handleResetPassword = async (user) => {
    const ok = window.confirm(
      `${user.email} のパスワードを再発行します。\n\n` +
        '本人確認は済んでいますか？\n' +
        '再発行するとこのユーザーは全端末でログアウトされ、現在のパスワードは使えなくなります。',
    );
    if (!ok) return;

    try {
      const res = await axios.post(
        Reset_Password_API_URL(user.id),
        {},
        authConfig(),
      );
      setIssued(res.data);
    } catch (error) {
      notifyError(error, 'パスワードの再発行に失敗しました');
    }
  };

  const handleDelete = async (email) => {
    try {
      // 副作用のある操作なので DELETE で叩く（サーバ側も GET から変更済み）
      await axios.delete(Delete_API_URL, {
        ...authConfig(),
        params: { email },
      });
    } catch (error) {
      notifyError(error, 'ユーザーの削除に失敗しました');
      return;
    }
    fetchUsers();
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>
        ユーザー管理
      </Typography>

      <Box mb={4}>
        <Typography variant="h6" gutterBottom>
          利用状況
        </Typography>
        {usageStatsError && <Typography color="error">利用状況を取得できませんでした</Typography>}
        {usageStats && (
          <>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <StatCard
                label="今日のログイン人数"
                value={`${usageStats.today.users} 人`}
                note={`${usageStats.today.logins} 回`}
              />
              <StatCard
                label="累計ログイン人数"
                value={`${usageStats.all_time.users} 人`}
                note={
                  usageStats.all_time.since
                    ? `${usageStats.all_time.logins} 回（${usageStats.all_time.since} から）`
                    : 'まだ記録がありません'
                }
              />
              <StatCard
                label="有料会員"
                value={`${usageStats.paying_users} 人`}
                note="定期課金が契約中"
              />
            </Box>
            <Typography variant="subtitle2" gutterBottom>
              日ごとのログイン（直近{LOGIN_STATS_DAYS}日）
            </Typography>
            <TableContainer component={Paper} sx={{ maxWidth: 420 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>日付</TableCell>
                    <TableCell align="right">ログイン回数</TableCell>
                    <TableCell align="right">人数</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageStats.items.map((item) => (
                    <TableRow key={item.date}>
                      <TableCell>{item.date}</TableCell>
                      <TableCell align="right">{item.logins}</TableCell>
                      <TableCell align="right">{item.users}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <Box mb={3} display="flex" gap={2}>
        <TextField
          label="user_id(空欄で自動採番)"
          name="user_id"
          value={form.user_id ?? ''}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: '0.75rem' } }} // 👈 ここでサイズ指定
        />
        <TextField
          label="Email"
          name="email"
          value={form.email}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: '0.75rem' } }} // 👈 ここでサイズ指定
        />
        <TextField
          label="ユーザー名"
          name="username"
          value={form.username}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: '0.75rem' } }} // 👈 ここでサイズ指定
        />
        <TextField
          label="パスワード"
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: '0.75rem' } }} // 👈 ここでサイズ指定
        />
        <TextField
          label="サービス名"
          name="memo"
          type="memo"
          value={form.memo}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: '0.75rem' } }} // 👈 ここでサイズ指定
        />
        <TextField
          label="メモ(オプション)"
          name="service_name"
          type="service_name"
          value={form.service_name}
          onChange={handleChange}
          InputLabelProps={{ sx: { fontSize: '0.75rem' } }} // 👈 ここでサイズ指定
        />
        <Button variant="contained" onClick={handleRegister}>
          登録
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>ユーザー名</TableCell>
              <TableCell>サービス名</TableCell>
              <TableCell>メモ</TableCell>
              <TableCell>契約状態</TableCell>
              <TableCell>コンテンツ</TableCell>
              <TableCell>期間末</TableCell>
              <TableCell>解約予約</TableCell>
              <TableCell align="right">ログイン回数</TableCell>
              <TableCell>最終ログイン</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.service_name}</TableCell>
                <TableCell>{user.sub_info}</TableCell>
                <TableCell>{billingStatusLabel(user.billing_status)}</TableCell>
                <TableCell>{(user.active_contents || []).map(contentLabel).join(', ')}</TableCell>
                <TableCell>{formatDate(user.current_period_end)}</TableCell>
                <TableCell>{user.cancel_at_period_end ? 'あり' : ''}</TableCell>
                <TableCell align="right">{user.login_count ?? 0}</TableCell>
                <TableCell>{formatDateTime(user.last_login_at)}</TableCell>
                <TableCell>
                  <Button onClick={() => handleResetPassword(user)}>
                    パスワード再発行
                  </Button>
                  <Button color="error" onClick={() => handleDelete(user.email)}>
                    削除
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="表示件数"
        />
      </TableContainer>

      <Dialog open={Boolean(issued)} onClose={() => setIssued(null)}>
        <DialogTitle>仮パスワードを発行しました</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <strong>この画面を閉じると二度と表示されません。</strong>
            <br />
            本人に伝えたうえで、ログイン後に画面右上の「パスワード変更」から
            ご自身のパスワードへ変更するよう案内してください。
          </DialogContentText>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              対象
            </Typography>
            <Typography>{issued?.email}</Typography>
          </Box>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              仮パスワード
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1.25rem' }}>
              {issued?.temporary_password}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssued(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagementPage;
